<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="breadcrumbs" />
        <h1 class="page-title">Tables</h1>
        <p class="page-subtitle">{{ rows.length }} table{{ rows.length > 1 ? 's' : '' }}</p>
      </div>
      <v-btn color="primary" depressed @click="openNew">
        <v-icon left small>mdi-plus</v-icon>
        Nouvelle table
      </v-btn>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <div class="d-flex justify-space-between align-center mb-3 flex-wrap" style="gap: 8px;">
        <v-chip-group v-model="filter" mandatory active-class="primary--text">
          <v-chip small outlined>Toutes ({{ rows.length }})</v-chip>
          <v-chip small outlined color="success">Actives ({{ activeCount }})</v-chip>
          <v-chip small outlined color="grey">Inactives ({{ rows.length - activeCount }})</v-chip>
          <v-chip small outlined color="warning">Occupées ({{ occupiedCount }})</v-chip>
          <v-chip small outlined color="success">Disponibles ({{ availableCount }})</v-chip>
        </v-chip-group>
        <input v-model="search" type="text" placeholder="Rechercher..." class="search-input" />
      </div>

      <v-card flat class="table-card">
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">NOM</th>
              <th class="text-left">ZONE</th>
              <th class="text-center">CAPACITÉ</th>
              <th class="text-center">OCCUPATION</th>
              <th class="text-center">STATUT</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in filteredRows" :key="t.id">
              <td class="text-left font-weight-medium">{{ t.name }}</td>
              <td class="text-left text-caption">{{ t.zone || '—' }}</td>
              <td class="text-center">{{ t.capacity }}</td>
              <td class="text-center">
                <v-chip x-small :color="t.status === 'occupied' ? 'warning' : 'success'" dark>
                  {{ t.status === 'occupied' ? 'Occupée' : 'Disponible' }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip x-small :color="t.active ? 'success' : 'error'" dark>{{ t.active ? 'Active' : 'Inactive' }}</v-chip>
              </td>
              <td class="text-right" style="white-space:nowrap;">
                <v-btn icon x-small @click="viewTable(t)" title="Voir" class="mr-1" v-if="t.status === 'occupied'">
                  <v-icon small color="warning">mdi-eye</v-icon>
                </v-btn>
                <v-btn icon x-small @click="edit(t)" title="Modifier" class="mr-1">
                  <v-icon small color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon x-small @click="confirmDelete(t)" title="Supprimer">
                  <v-icon small color="error">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr v-if="filteredRows.length === 0">
              <td colspan="6" class="text-center text--secondary pa-6">Aucune table trouvée</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </div>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="dialog" max-width="450" @click:outside="close">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">{{ index > -1 ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ form_title }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <div class="form-group">
            <label class="form-label">Nom de la table *</label>
            <v-text-field v-model="table.name" outlined dense hide-details="auto" placeholder="Ex: Table 1, Terrasse A"></v-text-field>
          </div>
          <div class="form-group">
            <label class="form-label">Zone / Section</label>
            <v-text-field v-model="table.zone" outlined dense hide-details="auto" placeholder="Ex: Terrasse, Salle, Bar"></v-text-field>
          </div>
          <v-row>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Capacité</label>
                <v-text-field v-model.number="table.capacity" outlined dense hide-details="auto" type="number" min="1"></v-text-field>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="form-group">
                <label class="form-label">Statut</label>
                <v-switch v-model="table.active" :label="table.active ? 'Active' : 'Inactive'" dense hide-details></v-switch>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="loading" @click="save">
            {{ index > -1 ? 'Modifier' : 'Créer' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="350">
      <v-card>
        <v-card-title class="text-h6">Supprimer cette table ?</v-card-title>
        <v-card-text>Cette action est irréversible.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Annuler</v-btn>
          <v-btn color="error" depressed @click="deleteItem">Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PreloaderComponent from '../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'

export default {
  name: 'TablesView',
  components: { PreloaderComponent, BreadcrumbComponent },
  data: () => ({
    search: '',
    filter: 0,
    index: -1,
    loading: false,
    dialog: false,
    deleteDialog: false,
    enable: false,
    rows: [],
    table: { name: '', zone: '', capacity: 4, active: true },
    deleteTarget: null,
    form_title: 'Nouvelle Table',
    store_id: null,
    breadcrumbs: [
      { text: 'Restaurant', disabled: true },
      { text: 'Tables', disabled: false },
    ],
  }),
  computed: {
    activeCount() {
      return this.rows.filter(r => r.active).length
    },
    occupiedCount() {
      return this.rows.filter(r => r.status === 'occupied').length
    },
    availableCount() {
      return this.rows.filter(r => r.status !== 'occupied').length
    },
    filteredRows() {
      let list = this.rows
      if (this.filter === 1) list = list.filter(r => r.active)
      if (this.filter === 2) list = list.filter(r => !r.active)
      if (this.filter === 3) list = list.filter(r => r.status === 'occupied')
      if (this.filter === 4) list = list.filter(r => r.status !== 'occupied')
      if (!this.search.trim()) return list
      const q = this.search.toLowerCase()
      return list.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.zone || '').toLowerCase().includes(q)
      )
    },
  },
  methods: {
    load() {
      this.$axios.get('/restaurant-tables?orderasc=name')
        .then(r => { this.rows = r.data; this.enable = true })
        .catch(e => { console.error('Error loading tables:', e); this.enable = true })
    },
    viewTable(table) {
      this.$router.push({ name: 'table-detail', params: { id: table.id } })
    },
    openNew() { this.reset(); this.dialog = true },
    edit(item) {
      this.index = this.rows.indexOf(item)
      this.table = Object.assign({}, item)
      this.form_title = 'Modifier : ' + this.table.name
      this.dialog = true
    },
    save() {
      if (!this.table.name) return
      this.loading = true
      var self = this

      this.table.store_id = this.store_id

      if (this.index > -1) {
        this.$axios.put('/restaurant-tables/' + this.table.id + '?data=' + encodeURIComponent(JSON.stringify(self.table)))
          .then(function (response) {
            Object.assign(self.rows[self.index], response.data)
            self.dialog = false; self.loading = false
            self.flashMessage.success({ title: 'Succès', message: 'Table modifiée' })
            self.reset()
          }).catch(function () { self.loading = false })
      } else {
        this.$axios.post('/restaurant-tables?data=' + encodeURIComponent(JSON.stringify(self.table)))
          .then(function (response) {
            self.rows.push(response.data)
            self.dialog = false; self.loading = false
            self.flashMessage.success({ title: 'Succès', message: 'Table créée' })
            self.reset()
          }).catch(function () { self.loading = false })
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item
      this.deleteDialog = true
    },
    deleteItem() {
      if (!this.deleteTarget) return
      var self = this
      this.$axios.delete('/restaurant-tables/' + this.deleteTarget.id)
        .then(function () {
          self.rows = self.rows.filter(r => r.id !== self.deleteTarget.id)
          self.deleteDialog = false
          self.deleteTarget = null
          self.flashMessage.success({ title: 'Succès', message: 'Table supprimée' })
        })
    },
    close() { this.dialog = false; this.reset(); this.loading = false },
    reset() {
      this.table = { name: '', zone: '', capacity: 4, active: true }
      this.index = -1
      this.form_title = 'Nouvelle Table'
    },
    getStore() {
      try {
        var business_id = JSON.parse(localStorage.business).id
        var self = this
        this.$axios.get('/stores?business_id=' + business_id)
          .then(function (response) {
            if (response.data && response.data.length > 0) {
              self.store_id = response.data[0].id
            }
          })
      } catch (e) { console.error(e) }
    },
  },
  mounted() { this.load(); this.getStore() },
}
</script>

<style scoped>
.search-input {
  width: 260px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.85rem;
  transition: border-color 0.15s;
}
.search-input:focus {
  outline: none;
  border-color: var(--v-primary-base, #194173);
}
</style>
